import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/apiClient';
import Reactions from './Reactions';
import { linkForItem, linkLabelForItem } from '../utils/deepLink';
import { jobName } from '../utils/ui';

// Drop the leading "<number> - " from a job name so it isn't repeated next to the number.
const shortJob = n => (n || '').replace(/^\s*\S+\s*-\s*/, '');

// Two pills for the top of a board/page:
//   • "<Page> Activity" — a timestamped who-did-what feed, scoped to this board.
//   • "<Page> Summary" / custom label — placeholder for the AI summary Scotto
//     will wire up; for now it opens and says "Upcoming".
//
// Props: pageName, scope ('job'|'shop'|'equipment'|'bids'|'trucking'…),
//        scopeId (optional — e.g. a project id for a job), summaryLabel (opt).
function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso); if (isNaN(d)) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
const fullStamp = iso => { const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };

// variant: 'light' (default, tinted pills) | 'onDark' (uniform translucent-white
//   pills for use on a dark banner). leading: extra pills rendered first, e.g.
//   [{ icon:'📍', label:'Location', onClick }] — styled to match so a row of pills
//   all look the same.
// variant 'pillows': the cards' look — two across, equal rounded tiles (Ryan, Oct 26 2026, the phone
//   boards). trailing: more tiles (nodes) in the same grid, e.g. the NVE board's Upcoming Inspections.
export default function ActivityPills({ pageName, scope, scopeId, summaryLabel, variant = 'light', leading = [], trailing = null, endpoint, linkJobs = true }) {
  const [open, setOpen] = useState(null); // 'activity' | 'summary' | null
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // ── "Daily Update" pill state ──────────────────────────────────────────────
  // A job page (scope 'job' with an id) posts straight to that job; every other
  // page shows a job picker so the update still lands on a real job's Daily
  // Updates + Activity (same place the mobile Daily Report goes).
  const jobFixed = scope === 'job' && scopeId;
  const [compose, setCompose] = useState(false);
  const [note, setNote] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(jobFixed ? scopeId : '');
  const [posting, setPosting] = useState(false);
  const [postedOk, setPostedOk] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const voiceOK = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  function openCompose() {
    setPostedOk(false); setNote(''); setCompose(true);
    setJobId(jobFixed ? scopeId : '');
    if (!jobFixed && jobs.length === 0) {
      apiFetch('/projects?scope=current').then(d => setJobs(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }
  function startVoice() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice input is not supported on this browser. You can still type.'); return; }
    const r = new SR();
    r.lang = 'en-US'; r.continuous = true; r.interimResults = false;
    r.onresult = e => { const t = Array.from(e.results).map(x => x[0].transcript).join(' '); setNote(n => n ? n + ' ' + t : t); };
    r.onend = () => setListening(false); r.onerror = () => setListening(false);
    r.start(); recRef.current = r; setListening(true);
  }
  function stopVoice() { recRef.current?.stop(); setListening(false); }
  async function postUpdate() {
    const targetJob = jobFixed ? scopeId : jobId;
    if (posting || !note.trim() || !targetJob) return;
    stopVoice(); setPosting(true);
    // Echo to this page's own feed too — but not for the aggregate pages that use
    // a custom endpoint (Company / PM), and not for the job feed (already logged).
    const board = (!endpoint && scope && scope !== 'job') ? scope : undefined;
    try {
      await apiFetch(`/projects/${targetJob}/daily-updates`, { method: 'POST', body: JSON.stringify({ notes: note.trim(), ...(board ? { board_scope: board } : {}) }) });
      setNote(''); setCompose(false); setPostedOk(true);
      if (open === 'activity') openActivity(); // refresh the feed if it's showing
    } catch (e) { alert(e?.message || 'Could not post the update. Please try again.'); }
    setPosting(false);
  }

  // ── ✨ Summary panel ────────────────────────────────────────────────────────
  // The AI summary for this board/page, written by the external AI layer.
  const [summary, setSummary] = useState(null);      // { summary, generated_at } | {} | null
  const [summaryLoading, setSummaryLoading] = useState(false);
  function openSummary() {
    setOpen('summary'); setSummaryLoading(true);
    const qs = scopeId ? `?scopeId=${encodeURIComponent(scopeId)}` : '';
    apiFetch(`/summaries/${encodeURIComponent(scope)}${qs}`)
      .then(d => setSummary(d && typeof d === 'object' ? d : {}))
      .catch(() => setSummary({}))
      .finally(() => setSummaryLoading(false));
  }

  function openActivity() {
    setOpen('activity'); setLoading(true);
    // `endpoint` overrides the default scope query (e.g. company-wide or PM-scoped feeds).
    const path = endpoint || `/activity?${new URLSearchParams({ scope, ...(scopeId ? { scopeId } : {}) }).toString()}`;
    apiFetch(path).then(d => setItems(Array.isArray(d) ? d : [])).catch(() => setItems([])).finally(() => setLoading(false));
  }

  const onDark = variant === 'onDark';
  const tiles = variant === 'pillows';
  const pill = (bg, color, border, label, onClick, icon) => (
    <button onClick={onClick}
      style={tiles
        ? { minHeight: 44, boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 10, border: `1.5px solid ${border}`, background: bg, color, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '0 10px', textAlign: 'center' }
        : { display: 'inline-flex', alignItems: 'center', gap: 6, background: onDark ? 'rgba(255,255,255,0.15)' : bg, color: onDark ? '#fff' : color, border: `1px solid ${onDark ? 'rgba(255,255,255,0.35)' : border}`, borderRadius: 999, padding: '6px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
      <span style={{ fontSize: 13 }}>{icon}</span>{label}
    </button>
  );

  return (
    <>
      <div style={tiles ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } : { display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {leading.map((l, i) => pill('#eef2ff', '#3730a3', '#c7d2fe', l.label, l.onClick, l.icon))}
        {pill('#eef2ff', '#3730a3', '#c7d2fe', `${pageName} Activity`, openActivity, '🕒')}
        {pill('#ecfdf5', '#065f46', '#a7f3d0', summaryLabel || `${pageName} Summary`, openSummary, '✨')}
        {pill('#fff7ed', '#9a3412', '#fed7aa', 'Daily Update', openCompose, '➕')}
        {trailing}
        {postedOk && <span style={{ alignSelf: 'center', fontSize: 12, fontWeight: 700, color: onDark ? '#fff' : '#15803d', ...(tiles ? { gridColumn: '1 / -1', textAlign: 'center' } : {}) }}>✓ Posted</span>}
      </div>

      {compose && createPortal(
        <div onClick={() => { stopVoice(); setCompose(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 480, borderRadius: '18px 18px 0 0', padding: '18px 18px 26px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>➕ Daily Update</div>
              <button onClick={() => { stopVoice(); setCompose(false); }} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>

            {jobFixed ? (
              <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>Posting to <b style={{ color: '#111827' }}>this job’s</b> Daily Updates.</div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>Job</label>
                <select value={jobId} onChange={e => setJobId(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 12px', fontSize: 15, background: '#fff', color: '#111827' }}>
                  <option value="">— Pick a job —</option>
                  {jobs.map(p => <option key={p.id} value={p.id}>{`${p.project_number ? p.project_number + ' · ' : ''}${jobName(p.name || p.job_name) || 'Job'}`}</option>)}
                </select>
              </div>
            )}

            <div style={{ position: 'relative' }}>
              <textarea value={note} autoFocus placeholder="What happened today?" onChange={e => setNote(e.target.value)} rows={4}
                style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '12px', paddingRight: 52, fontSize: 16, resize: 'vertical', fontFamily: 'inherit', color: '#111827', background: '#fff' }} />
              {voiceOK && (
                <button onClick={() => listening ? stopVoice() : startVoice()} title={listening ? 'Stop' : 'Tap to talk'}
                  style={{ position: 'absolute', top: 8, right: 8, width: 38, height: 38, borderRadius: '50%', border: 'none', cursor: 'pointer', background: listening ? '#dc2626' : '#eef2ff', color: listening ? '#fff' : '#1e3a5f', fontSize: 18, lineHeight: 1 }}>🎤</button>
              )}
            </div>
            {listening && <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginTop: 6 }}>● Listening… tap the mic to stop.</div>}

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={postUpdate} disabled={posting || !note.trim() || (!jobFixed && !jobId)}
                style={{ flex: 1, padding: '13px', borderRadius: 10, border: 'none', background: (note.trim() && (jobFixed || jobId)) ? '#15803d' : '#cbd5e1', color: '#fff', fontSize: 15, fontWeight: 800, cursor: (note.trim() && (jobFixed || jobId)) ? 'pointer' : 'default' }}>
                {posting ? 'Posting…' : 'Post Update'}
              </button>
              <button onClick={() => { stopVoice(); setCompose(false); }}
                style={{ padding: '13px 18px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>Shows in the job’s Daily Updates{(!endpoint && scope && scope !== 'job') ? ` and in ${pageName} Activity` : ' and Activity'}{voiceOK ? ' · tap 🎤 to dictate' : ''}.</div>
          </div>
        </div>,
        document.body
      )}

      {open && (
        <div onClick={() => setOpen(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 4000, display: 'flex', justifyContent: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: '100%', height: '100%', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: open === 'activity' ? '#3730a3' : '#065f46', color: '#fff', padding: '16px 18px', paddingTop: 'max(env(safe-area-inset-top), 16px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>{open === 'activity' ? `${pageName} Activity` : (summaryLabel || `${pageName} Summary`)}</div>
              <button onClick={() => setOpen(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {open === 'summary' ? (
                summaryLoading ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>Loading…</div>
                ) : summary && summary.summary ? (
                  <div>
                    <div style={{ fontSize: 14, lineHeight: 1.65, color: '#1f2937' }}>
                      {String(summary.summary).split(/\n{2,}/).map((p, i) => (
                        <p key={i} style={{ margin: '0 0 12px' }}>{p.trim()}</p>
                      ))}
                    </div>
                    {summary.generated_at && (
                      <div title={fullStamp(summary.generated_at)}
                        style={{ marginTop: 6, paddingTop: 10, borderTop: '1px solid #f3f4f6', fontSize: 11.5, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>✨</span> Generated {relTime(summary.generated_at)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', paddingTop: 60, color: '#6b7280' }}>
                    <div style={{ fontSize: 44, marginBottom: 12 }}>✨</div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>Upcoming</div>
                    <p style={{ fontSize: 14, marginTop: 8, lineHeight: 1.5 }}>An AI summary of what's recently happened and what's coming up on this board will live here.</p>
                  </div>
                )
              ) : loading ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>Loading…</div>
              ) : items.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>No activity logged yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {items.map(a => {
                    // Open what the entry is actually about (the PO, the drilling
                    // card, the submittal) — falling back to the job when that's
                    // all we know. `linkJobs=false` keeps a board's own feed inert.
                    const target = linkJobs ? linkForItem({ scope: a.scope, projectId: a.project_id, data: a.meta }) : null;
                    const clickable = !!target;
                    return (
                      <div key={a.id} onClick={clickable ? () => { setOpen(null); navigate(target); } : undefined}
                        style={{ display: 'flex', gap: 10, padding: '10px 6px', borderBottom: '1px solid #f3f4f6', cursor: clickable ? 'pointer' : 'default', borderRadius: 8 }}
                        onMouseEnter={clickable ? e => e.currentTarget.style.background = '#f9fafb' : undefined}
                        onMouseLeave={clickable ? e => e.currentTarget.style.background = 'transparent' : undefined}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#818cf8', marginTop: 6, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {a.job_number && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                              <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 12, color: '#1e3a5f', background: '#eff6ff', borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap' }}>{a.job_number}</span>
                              {a.job_name && <span style={{ fontSize: 11.5, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortJob(a.job_name)}</span>}
                            </div>
                          )}
                          <div style={{ fontSize: 13.5, color: '#111827', lineHeight: 1.4 }}>
                            <strong>{a.actor_name}</strong> {a.action}
                          </div>
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }} title={fullStamp(a.created_at)}>
                            {relTime(a.created_at)}{clickable && <span style={{ color: '#818cf8', fontWeight: 700 }}>· {linkLabelForItem({ scope: a.scope, data: a.meta })} ›</span>}
                          </div>
                          <Reactions type={a.react?.type} id={a.react?.id} reactions={a.reactions} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
